import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Box,
  Divider,
  Tabs,
  Tab
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import SendIcon from "@material-ui/icons/Send";
import CodeIcon from "@material-ui/icons/Code";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ApiDocsModal = ({ open, onClose, whatsApps = [] }) => {
  const [token, setToken] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [selectedWappId, setSelectedWappId] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Prompt para o Agente, 1: Exemplos de Código, 2: Documentação Técnica

  useEffect(() => {
    const fetchApiToken = async () => {
      try {
        const { data } = await api.get("/settings");
        const tokenSetting = data?.find(s => s.key === "userApiToken");
        if (tokenSetting?.value) {
          setToken(tokenSetting.value);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchApiToken();
    }
  }, [open]);

  // Se houver conexão dra stefanie fortaleza, seleciona como padrão caso o usuário não tenha escolhido
  useEffect(() => {
    if (open && whatsApps && whatsApps.length > 0 && selectedWappId === null) {
      const stefanieWapp = whatsApps.find(w => w.name && w.name.includes("stefanie"));
      if (stefanieWapp) {
        setSelectedWappId(stefanieWapp.id);
      } else {
        setSelectedWappId(whatsApps[0].id);
      }
    }
  }, [open, whatsApps, selectedWappId]);

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success("Copiado com sucesso!");
    setTimeout(() => setCopiedKey(""), 2500);
  };

  const getPhoneNumber = (w) => {
    try {
      if (w.session) {
        const parsed = JSON.parse(w.session);
        const jid = parsed?.me?.id || "";
        return jid.split(":")[0] || jid.split("@")[0] || "Número não extraído";
      }
    } catch (e) {}
    return "Número na sessão";
  };

  const activeWapps = (whatsApps || []).filter(w => w.status === "CONNECTED");
  const currentWapp =
    whatsApps.find(w => w.id === selectedWappId) ||
    activeWapps[0] ||
    (whatsApps && whatsApps[0]) ||
    { id: 7, name: "drastefanie-fortaleza", session: "" };

  const currentPhone = getPhoneNumber(currentWapp);
  const endpointUrl = "https://zap.condemkt.com/api/messages/send";

  // Prompt Mastigado Pronto para Enviar ao Agente Programador
  const promptForAgent = `Olá, agente! Preciso que você configure o disparo automático de WhatsApp no site da ${currentWapp.name.includes("stefanie") ? "Dra. Stefanie Viana" : currentWapp.name}.

Sempre que um usuário preencher o formulário ou for capturado um novo lead no site, faça um disparo HTTP POST para nossa API oficial do CondeCRM com as seguintes credenciais e regras:

### 1. DADOS DA CONEXÃO E ENDPOINT
- **Endpoint:** POST ${endpointUrl}
- **Header de Autenticação:** Authorization: Bearer ${token || "SEU_TOKEN_AQUI"}
- **Header de Conteúdo:** Content-Type: application/json
- **Conexão Remetente:** Usar obrigatoriamente a conexão "${currentWapp.name}" (ID ${currentWapp.id}, número pareado: ${currentPhone})

### 2. ESTRUTURA DO PAYLOAD JSON
\`\`\`json
{
  "whatsappId": ${currentWapp.id},
  "number": "<NUMERO_DE_DESTINO_COM_DDD>",
  "body": "🚀 *Novo Lead Capturado no Site!*\\n\\n*Nome:* {nome}\\n*Telefone:* {telefone}\\n*Interesse:* {servico}\\n*Origem:* Formulário do Site"
}
\`\`\`

### 3. REGRAS DE IMPLEMENTAÇÃO
- O campo \`whatsappId\` deve ser estritamente ${currentWapp.id}.
- O campo \`number\` deve conter o DDI + DDD + número (ex: 5585986864048 ou o número do cliente), apenas números.
- O campo \`body\` contém o texto da mensagem formatado em WhatsApp Markdown (*negrito*, quebras de linha com \\n).
- Trate a resposta: a API retorna HTTP 200 com \`{ "success": true, "ticketId": ..., "fromWhatsapp": ... }\`.
- Se ocorrer erro na chamada, registre no console/log sem travar a experiência do usuário na página de obrigado.`;

  const sampleJson = {
    whatsappId: currentWapp.id,
    number: "5585986864048",
    body: `🚀 *Novo Lead - Site ${currentWapp.name.includes("stefanie") ? "Dra. Stefanie" : currentWapp.name}*\n\n*Nome:* Maria Clara\n*Telefone:* (85) 99999-9999\n*Interesse:* Assistência ao Parto / Pré-Natal`
  };

  const sampleCurl = `curl -X POST "${endpointUrl}" \\
  -H "Authorization: Bearer ${token || "SEU_TOKEN_AQUI"}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(sampleJson, null, 2)}'`;

  const sampleJs = `// Exemplo em Server Action, Next.js API Route ou Express:
export async function sendLeadWhatsAppNotification(leadData) {
  const response = await fetch("${endpointUrl}", {
    method: "POST",
    headers: {
      "Authorization": "Bearer ${token || "SEU_TOKEN_AQUI"}",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      whatsappId: ${currentWapp.id}, // Conexão "${currentWapp.name}"
      number: "5585986864048", // Número do admin ou do cliente
      body: \`🚀 *Novo Lead Capturado no Site*\\n\\n*Nome:* \${leadData.name}\\n*Telefone:* \${leadData.phone}\\n*Interesse:* \${leadData.interest || "Geral"}\`
    })
  });

  return await response.json();
}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ backgroundColor: "#0f172a", color: "#f8fafc", padding: "14px 20px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gridGap={10}>
            <SendIcon style={{ color: "#38bdf8" }} />
            <div>
              <Typography variant="h6" style={{ fontWeight: 800, fontSize: "1.1rem" }}>
                Gerador de Prompt & Integração de API de Disparos
              </Typography>
              <Typography variant="caption" style={{ color: "#94a3b8" }}>
                Selecione a conexão desejada abaixo para gerar o comando pronto para o seu agente programador
              </Typography>
            </div>
          </Box>
          <IconButton size="small" onClick={onClose} style={{ color: "#94a3b8" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: "18px 22px", backgroundColor: "#f8fafc" }}>
        {/* 1. SELETOR DE CONEXÃO REMETENTE */}
        <Paper variant="outlined" style={{ padding: "12px 16px", marginBottom: 16, backgroundColor: "#ffffff", borderRadius: 8 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
            <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 6 }}>
              <PhoneAndroidIcon fontSize="small" style={{ color: "#0284c7" }} /> 
              1º Passo: Escolha qual WhatsApp fará os disparos
            </Typography>
            <Chip
              label={`Selecionado: ${currentWapp.name} (ID ${currentWapp.id})`}
              color="primary"
              size="small"
              style={{ fontWeight: 700 }}
            />
          </Box>
          <Typography variant="caption" style={{ color: "#64748b", display: "block", marginBottom: 12 }}>
            Clique na conexão que você deseja que envie as mensagens (ex: <strong>drastefanie-fortaleza</strong> para o site da Dra. Stefanie):
          </Typography>

          <Table size="small">
            <TableHead style={{ backgroundColor: "#f1f5f9" }}>
              <TableRow>
                <TableCell style={{ fontWeight: 700, fontSize: "0.75rem", width: 50 }}>Sel.</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>ID</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>Nome da Conexão</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>Número Conectado</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(whatsApps || []).map(w => {
                const phone = getPhoneNumber(w);
                const isSelected = currentWapp.id === w.id;
                return (
                  <TableRow
                    key={w.id}
                    hover
                    onClick={() => setSelectedWappId(w.id)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#f0f9ff" : "inherit"
                    }}
                  >
                    <TableCell>
                      {isSelected ? (
                        <RadioButtonCheckedIcon style={{ color: "#0284c7", fontSize: 20 }} />
                      ) : (
                        <RadioButtonUncheckedIcon style={{ color: "#94a3b8", fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontWeight: 700 }}>
                      <Chip label={`ID ${w.id}`} size="small" style={{ fontWeight: 800, backgroundColor: isSelected ? "#bae6fd" : "#e2e8f0" }} />
                    </TableCell>
                    <TableCell style={{ fontWeight: isSelected ? 800 : 600, color: isSelected ? "#0369a1" : "#1e293b" }}>
                      {w.name}
                    </TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={w.status === "CONNECTED" ? "Conectado" : w.status}
                        size="small"
                        style={{
                          backgroundColor: w.status === "CONNECTED" ? "#dcfce7" : "#fee2e2",
                          color: w.status === "CONNECTED" ? "#166534" : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        {/* 2. ABAS DE VISUALIZAÇÃO */}
        <Box borderBottom={1} borderColor="divider" marginBottom={2}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              label="📋 Prompt Mastigado para o Agente"
              icon={<AssignmentTurnedInIcon fontSize="small" />}
              style={{ textTransform: "none", fontWeight: 700 }}
            />
            <Tab
              label="💻 Código JS / Fetch / cURL"
              icon={<CodeIcon fontSize="small" />}
              style={{ textTransform: "none", fontWeight: 700 }}
            />
            <Tab
              label="🔑 Detalhes da API & Token"
              icon={<VpnKeyIcon fontSize="small" />}
              style={{ textTransform: "none", fontWeight: 700 }}
            />
          </Tabs>
        </Box>

        {/* ABA 0: PROMPT PRONTO PARA O AGENTE PROGRAMADOR */}
        {activeTab === 0 && (
          <Paper variant="outlined" style={{ padding: 16, backgroundColor: "#ffffff", borderRadius: 8 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1.5}>
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#1e293b" }}>
                  Instrução Pronta para Enviar ao Agente de IA do Site
                </Typography>
                <Typography variant="caption" style={{ color: "#64748b" }}>
                  Basta clicar em <strong>"Copiar Prompt para o Agente"</strong> e colar diretamente no chat do seu programador:
                </Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={copiedKey === "prompt" ? <CheckIcon style={{ color: "#ffffff" }} /> : <FileCopyIcon />}
                onClick={() => handleCopy(promptForAgent, "prompt")}
                style={{ textTransform: "none", fontWeight: 700, backgroundColor: "#0284c7" }}
              >
                {copiedKey === "prompt" ? "Prompt Copiado! ✅" : "Copiar Prompt para o Agente"}
              </Button>
            </Box>

            <Box
              component="pre"
              style={{
                backgroundColor: "#0f172a",
                color: "#f1f5f9",
                padding: 16,
                borderRadius: 8,
                fontSize: "0.82rem",
                fontFamily: "Consolas, Monaco, monospace",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.5,
                margin: 0,
                border: "1px solid #334155"
              }}
            >
              {promptForAgent}
            </Box>
          </Paper>
        )}

        {/* ABA 1: CÓDIGO JS / CURL */}
        {activeTab === 1 && (
          <Paper variant="outlined" style={{ padding: 16, backgroundColor: "#ffffff", borderRadius: 8 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b" }}>
                Função TypeScript / JavaScript (Next.js / Node.js)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={copiedKey === "js" ? <CheckIcon style={{ color: "#16a34a" }} /> : <FileCopyIcon />}
                onClick={() => handleCopy(sampleJs, "js")}
                style={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700 }}
              >
                {copiedKey === "js" ? "Código Copiado!" : "Copiar Função JS"}
              </Button>
            </Box>

            <Box
              component="pre"
              style={{
                backgroundColor: "#1e293b",
                color: "#e2e8f0",
                padding: 14,
                borderRadius: 6,
                fontSize: "0.78rem",
                fontFamily: "Consolas, Monaco, monospace",
                overflowX: "auto",
                margin: "0 0 16px 0"
              }}
            >
              {sampleJs}
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b" }}>
                Comando cURL de Terminal
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={copiedKey === "curl" ? <CheckIcon style={{ color: "#16a34a" }} /> : <FileCopyIcon />}
                onClick={() => handleCopy(sampleCurl, "curl")}
                style={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700 }}
              >
                {copiedKey === "curl" ? "cURL Copiado!" : "Copiar cURL"}
              </Button>
            </Box>

            <Box
              component="pre"
              style={{
                backgroundColor: "#0f172a",
                color: "#38bdf8",
                padding: 14,
                borderRadius: 6,
                fontSize: "0.78rem",
                fontFamily: "Consolas, Monaco, monospace",
                overflowX: "auto",
                margin: 0
              }}
            >
              {sampleCurl}
            </Box>
          </Paper>
        )}

        {/* ABA 2: DETALHES DE AUTENTICAÇÃO */}
        {activeTab === 2 && (
          <Paper variant="outlined" style={{ padding: 16, backgroundColor: "#ffffff", borderRadius: 8 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>
              Credenciais Oficiais da API
            </Typography>

            <Box display="flex" flexDirection="column" gridGap={12}>
              <div>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#64748b" }}>
                  ENDPOINT OFICIAL (MÉTODO POST):
                </Typography>
                <Box display="flex" alignItems="center" gridGap={8} marginTop={0.5}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={endpointUrl}
                    InputProps={{ readOnly: true, style: { fontFamily: "monospace", fontSize: "0.85rem", backgroundColor: "#f1f5f9" } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={copiedKey === "url" ? <CheckIcon style={{ color: "#16a34a" }} /> : <FileCopyIcon />}
                    onClick={() => handleCopy(endpointUrl, "url")}
                    style={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Copiar
                  </Button>
                </Box>
              </div>

              <div>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#64748b" }}>
                  TOKEN DE AUTORIZAÇÃO (HEADER Authorization: Bearer &lt;token&gt;):
                </Typography>
                <Box display="flex" alignItems="center" gridGap={8} marginTop={0.5}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={token || "Carregando token..."}
                    InputProps={{ readOnly: true, style: { fontFamily: "monospace", fontSize: "0.85rem", backgroundColor: "#f1f5f9" } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={copiedKey === "token" ? <CheckIcon style={{ color: "#16a34a" }} /> : <FileCopyIcon />}
                    onClick={() => handleCopy(token, "token")}
                    style={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Copiar
                  </Button>
                </Box>
              </div>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 20px", backgroundColor: "#f1f5f9" }}>
        <Button onClick={onClose} variant="contained" color="primary" style={{ textTransform: "none", fontWeight: 700 }}>
          Concluído
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiDocsModal;
